import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import OverLayLoading, { Loading } from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { OldUserGridColumns } from "./utils/UserListGridColumns";
import { allUsersGroupRolesApi, UserListFilter } from "api/user";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import Filter from "./utils/OldUserListFilter";
import "./style.css";

import styles from "styles.module.css";
import { UserListItem } from "../../../interface/user";
import { COLOR, ICON_NAMES } from "utils/constant";
import { CustomError, IconButtonActionType } from "interface";
import { useInfoStore } from "store/error";
import { migrateOldUserToNewLoginApi, getUsersNotMigratedApi } from "api/administration";
import OldUserListMobileList from "./OldUsersListMobileList";

const OldUsersPage: FC<{}> = () => {
	const pageSize = 60;
	const { t } = useTranslation();
	const overlayRef = useRef<HTMLDivElement>(null);
	const checkItemsRef = useRef<HTMLDivElement>(null);
	const gridRef = useRef<HTMLDivElement>(null);
	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);
	const [isMobileView, setIsMobileView] = useState<boolean>(() => {
		if (typeof window === "undefined") {
			return false;
		}

		return window.innerWidth < 768;
	});
	const [data, setData] = useState<UserListItem[]>([]);
	const [mobileLoading, setMobileLoading] = useState<boolean>(false);

	const [filterOptions, setFilterOptions] = useState<UserListFilter>({
		take: pageSize,
		skip: 0,
	});
	const { setError, setInfo, resetStore } = useInfoStore();
	const [forceUpdate, setForceUpdate] = useState<boolean>(false);
	const mobileNextSkip = useRef(0);
	const [userGroupRoles, setUserGroupRoles] = useState<any[]>([]);

	// let { path, id, action } = useParams();

	const requestMobileData = (skip: number, take: number, resetData = false) => {
		setMobileLoading(true);

		const reqFilter = { ...filterOptions, skip, take };

		getUsersNotMigratedApi(reqFilter)
			.then((response) => {
				const incoming = (response?.data || []) as UserListItem[];

				setData(incoming);
			})
			.catch((error) => {
				if (error.response?.data) {
					if (error.response.data?.errors) {
						setError(new CustomError(error.response?.data.title));
					} else {
						setError(new CustomError(error.response?.data.returnText));
					}
				} else {
					setError(new CustomError(error.message));
				}
			})
			.finally(() => {
				setMobileLoading(false);
			});
	};

	const exportGridAsPDF = () => {
		if (pdfRef.current !== null) {
			pdfRef.current.save();
		}
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	useEffect(() => {
		allUsersGroupRolesApi()
			.then((response) => {
				setUserGroupRoles(response.data);
			})
			.catch((error) => {
				const err = new CustomError(t("ErrorTitle"));
				const showCustomError = true;
				setError(err, showCustomError);
				setUserGroupRoles([]);
			})
			.finally(() => {});
	}, []);

	useEffect(() => {
		getUsersNotMigratedApi(filterOptions)
			.then((response) => {
				setData(response.data);
			})
			.catch((error) => {
				const err = new CustomError(t("ErrorTitle"));
				const showCustomError = true;
				setError(err, showCustomError);
				setData([]);
			})
			.finally(() => {});
	}, []);

	useEffect(() => {
		if (!isMobileView) {
			return;
		}

		requestMobileData(0, pageSize, true);
	}, [isMobileView, filterOptions, forceUpdate]);

	const requestOldUserMigration = (data: UserListItem) => {
		setInfo({
			title: t("Migrate") + " " + t("User"),
			description: t("MigrationConfirmation"),
			show: true,
			actions: [
				{
					text: t("Cancel"),
					btn: () => {
						//console.log("Delete cancelled");
						resetStore();
					},
					textColor: COLOR.StatusRed,
				},
				{
					text: t("Migrate"),
					btn: () => {
						if (!data.id) {
							const err = new CustomError(
								t("ErrorDescription"),
								t("ErrorTitle"),
							);
							setError(err);
							resetStore();
						} else {
							handleOldUserMigration(data.id);
							resetStore();
						}
					},
					textColor: COLOR.StatusGreen,
				},
			],
		});
	};

	const handleOldUserMigration = (id: string) => {
		migrateOldUserToNewLoginApi({ userId: id as string })
			.then(() => {
				setForceUpdate((prev) => !prev);
				setInfo({
					title: t("Success"),
					description: t("MigrationSuccess"),
					show: true,
					actions: [
						{
							text: t("Ok"),
							btn: () => {
								resetStore();
							},
							textColor: COLOR.StatusGreen,
						},
					],
				});
			})
			.catch((error) => {
				const err = new CustomError(t("MigrationFailed"), t("ErrorTitle"));
				const showCustomError = true;
				setError(err, showCustomError);
			});
	};

	return (
		<div className={styles.PageContainer}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>{t("OldUsers")}</legend>
			</div>

			{
				<div ref={checkItemsRef}>
					<Filter
						setFilterOptions={setFilterOptions}
						filterOptions={filterOptions}
						exportAsPDF={exportGridAsPDF}
						exportAsExcel={exportGridAsExcel}
						pageSize={0}
						roles={userGroupRoles}
					/>
				</div>
			}
			<div style={{}} ref={gridRef}>
				{isMobileView ? (
					<OldUserListMobileList
						items={data}
						loading={mobileLoading}
						onLoadMore={() =>
							requestMobileData(mobileNextSkip.current, pageSize)
						}
						migrate={requestOldUserMigration}
					/>
				) : (
					<GridCustomPage
						dataKey="id"
						forceUpdate={forceUpdate}
						gridColumns={OldUserGridColumns()}
						exportGridAsPDFRef={pdfRef}
						exportGridAsExcelRef={excelRef}
						apiMethod={getUsersNotMigratedApi}
						itemAboveHeight={50}
						sortable={true}
						serverFilter={true}
						filter={filterOptions}
						showDetailIcon={false}
						lastColWidth="150px"
						lastColBtns={[
							{
								alwaysShow: true,
								onclick: (data) => {
									requestOldUserMigration(data.dataItem);
								},
								actionType: IconButtonActionType.Update,
								iconName: ICON_NAMES.DatabaseBackup,
							},
						]}
					/>
				)}
			</div>
		</div>
	);
};

export default OldUsersPage;
